import { Link } from 'react-router-dom';
import { CheckIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { useCartActions } from '../../contexts/StoreContext';
import { useCatalog } from '../../contexts/CatalogContext';
import type { Routine } from '../../core/types/catalog';
import { euro } from '../../core/format/currency';
import { listNl } from '../../core/format/text';
import { countSoldOut, isAllSoldOut, isSoldOut } from '../../core/rules/stock';
import { shopLabels } from '../../data/shopLabels';
import { btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';

/**
 * Protocolkaart (home, webshop, protocollen): claim, naam, bij welke klachten, verhaal, de
 * producten, wat erbij zit, en één knop met de prijs erin. De klachten komen uit de koppeling
 * in Aanbod › Huidklachten, zodat je zonder lezen ziet of dit voor jou is.
 */
export function RoutineCard({
  routine
}: {
  routine: Routine;
}) {
  const {
    addRoutine
  } = useCartActions();
  const {
    routineTotals,
    treatmentForRoutine,
    concerns
  } = useCatalog();
  const {
    items,
    price,
    months
  } = routineTotals(routine);
  const treatment = treatmentForRoutine(routine);
  const soldOutCount = countSoldOut(items);
  const allSoldOut = isAllSoldOut(items);
  const forConcerns = concerns.filter(c => c.routineId === routine.id).map(c => c.label.toLowerCase());
  return <article className="flex h-full flex-col" aria-labelledby={`routine-${routine.id}`} data-id="element-8787" data-mp="3atbrs5ly18gx">
      <div className="flex items-start justify-between gap-3" data-id="element-8788" data-mp="2drzp9x0dw3g">
        <p className="text-[15px] italic text-clay" data-id="element-8789" data-mp="7cmiwcyw1mo3">{routine.claim}</p>
        {routine.level === 'Compleet' && <span className="whitespace-nowrap rounded-full bg-clay px-3 py-1 text-[12px] text-shell" data-id="element-8790" data-mp="3otmh347luezz">Meest compleet</span>}
      </div>
      <h3 id={`routine-${routine.id}`} className="mt-2 type-title text-ink" data-id="element-8791" data-mp="3tq37k52a15g8">
        {routine.name}
      </h3>
      {forConcerns.length > 0 && <p className="mt-1.5 text-[14px] text-ink" data-id="element-8792" data-mp="q2ukgqmxw526">Bij {listNl(forConcerns)}</p>}
      <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-8793" data-mp="q4xedlr7qic6">{routine.story}</p>

      <ul className="mt-5 divide-y divide-line border-y border-line" aria-label={`Producten in ${routine.name}`} data-id="element-8794" data-mp="zy1p8m5rhdw1">
        {items.map(p => <li key={p.id} className="flex items-center gap-3 py-3" data-id="element-8795" data-mp="q042y47gpr62">
            <ProductImage src={p.image} alt="" width={40} className="h-12 w-10 shrink-0 p-1" data-id="element-8796" data-mp="q2hwg8mhuvbi" />
            <div className="min-w-0 flex-1" data-id="element-8797" data-mp="m14onszuci1y">
              <p className="text-[12px] text-clay" data-id="element-8798" data-mp="h08mbjv513yn">{isSoldOut(p) ? shopLabels.soldOutShort : p.step}</p>
              <Link to={`/webshop/${p.id}`} className="text-[15px] leading-snug text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-8799" data-mp="c8z24yscysu5">
                {p.name}
              </Link>
            </div>
            <span className="text-[14px] tabular-nums text-muted" data-id="element-8800" data-mp="2cdt82d7wxl1">{euro(p.price)}</span>
          </li>)}
      </ul>

      {routine.included.length > 0 && <ul className="mt-4 space-y-1.5" aria-label="Inbegrepen" data-id="element-8801" data-mp="3pg0s29o0rpod">
          {routine.included.map(inc => <li key={inc} className="flex items-start gap-2 text-[14px] leading-snug text-muted" data-id="element-8802" data-mp="24y9hh76abbo1">
              <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sage" strokeWidth={2} aria-hidden="true" data-id="element-8803" data-mp="29wb585ennz5u" />
              {inc}
            </li>)}
        </ul>}

      <div className="mt-auto pt-6" data-id="element-8804" data-mp="2ix92m4ul98fg">
        <p className="mb-3 text-[13px] text-muted" data-id="element-8805" data-mp="2iwg0xiahl946">Gaat {months} mee</p>
        <button type="button" onClick={() => addRoutine(routine.id)} disabled={allSoldOut} aria-label={allSoldOut ? `${routine.name} is tijdelijk uitverkocht` : `${routine.name} in winkelmand, ${euro(price)}`} className={btnPrice} data-id="element-8806" data-mp="1m7rznxmzq0hg">
          {allSoldOut ? <span className="w-full text-center" data-id="element-8807" data-mp="1zzonvqljfq8k">{shopLabels.soldOutShort}</span> : <>
              <span className={btnPriceLabel} data-id="element-8808" data-mp="12ihi0e3op5gp">Protocol in de mand</span>
              <span className={btnPriceAmount} data-id="element-8809" data-mp="17hkfndhc49en">{euro(price)}</span>
            </>}
        </button>
        {soldOutCount > 0 && !allSoldOut && <p className="mt-3 text-center text-[13px] text-muted" data-id="element-8810" data-mp="3a597tptmqtly">{shopLabels.partlySoldOut(soldOutCount)}</p>}
        {treatment && <p className="mt-3 text-center text-[13px] text-muted" data-id="element-8811" data-mp="3543h1xjj5gf2">Het thuisprotocol bij {treatment.name}</p>}
      </div>
    </article>;
}